import React, { useState, useEffect, useCallback } from "react";
import { Button, Tag, Space, Popconfirm, Typography } from "antd";
import { CheckSquareOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import DataTable from "./common/DataTable";
import ChecklistTemplateModal from "./Modals/checklistTemplateModal";
import { listAllChecklistTemplates, deleteChecklistTemplate } from "../network/api";
import { useAuth } from "../context/AuthContext";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";
import { VISIT_TYPE_LABEL, VISIT_TYPE_TAG_COLOR } from "../constants/visits";

const { Text } = Typography;

export default function ChecklistTemplates() {
  const { isHardwareAdmin, isSuperAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(await listAllChecklistTemplates());
    } catch (error) {
      notify.error(serverError(error, "Failed to load templates"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    try {
      await deleteChecklistTemplate(id);
      notify.success("Template deleted");
      load();
    } catch (error) {
      notify.error(serverError(error, "Failed to delete template"));
    }
  };

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setModalOpen(true); };

  const columns = [
    { title: "Name", dataIndex: "name", render: (v) => <Text strong>{v}</Text> },
    { title: "Visit type", dataIndex: "visitType", render: (t) => <Tag color={VISIT_TYPE_TAG_COLOR[t]}>{VISIT_TYPE_LABEL[t] || t}</Tag> },
    { title: "Items", dataIndex: "items", align: "center", render: (items) => <Tag>{items?.length || 0}</Tag> },
    { title: "Status", dataIndex: "isActive", render: (a) => (a === false ? <Tag color="red">Inactive</Tag> : <Tag color="green">Active</Tag>) },
    { title: "Updated", dataIndex: "updatedAt", render: (v) => (v ? relativeTime(v) : "—") },
    ...(isHardwareAdmin
      ? [{
          title: "",
          key: "actions",
          align: "right",
          render: (_, r) => (
            <Space>
              <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
              {isSuperAdmin && (
                <Popconfirm title="Delete this template?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => onDelete(r._id)}>
                  <Button type="link" danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              )}
            </Space>
          ),
        }]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Checklist Templates"
        subtitle="Define the checklists technicians fill on visits"
        icon={<CheckSquareOutlined />}
        extra={isHardwareAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>New Template</Button>}
      />

      <DataTable
        rowKey="_id"
        columns={columns}
        dataSource={templates}
        loading={loading}
        searchableKeys={["name", "visitType"]}
        searchPlaceholder="Search templates…"
        emptyText="No checklist templates yet"
      />

      <ChecklistTemplateModal
        open={modalOpen}
        template={editing}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
